import { useMemo, useState, type FormEvent } from 'react';
import type {
  AssignmentStatus,
  PersonnelClassification,
} from '@dsr/shared-types';
import {
  useAssignments,
  useCreateAssignment,
  useAssignAssignment,
  useTransitionAssignment,
  type Assignment,
} from '../api/queries/assignments';
import { useSites, usePosts } from '../api/queries/sites';
import { usePersonnelList } from '../api/queries/personnel';
import { usePrincipals } from '../api/queries/principals';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { DateInput } from '../components/DateInput';
import {
  Button,
  Dialog,
  FormField,
  FormRow,
  Select,
  StatusBadge,
  Textarea,
  type SelectOption,
} from '../components/ui';
import { fmtTime } from '../utils/date';
import { firstFieldError } from './IncidentsPage';
import './reports.css';
import './scheduling.css';

// ---------------------------------------------------------------------------
// Static labels / option lists
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<AssignmentStatus, string> = {
  open: 'Open',
  assigned: 'Assigned',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  completed: 'Completed',
  no_show: 'No show',
  canceled: 'Canceled',
};

const STATUS_OPTIONS: AssignmentStatus[] = [
  'open',
  'assigned',
  'confirmed',
  'in_progress',
  'completed',
  'no_show',
  'canceled',
];

// Mirrors the API state machine in routes/assignments.ts. Used to surface only
// the legal next moves as inline actions on each shift card.
const TRANSITIONS: Record<AssignmentStatus, AssignmentStatus[]> = {
  open: ['canceled'],
  assigned: ['confirmed', 'no_show', 'canceled'],
  confirmed: ['in_progress', 'no_show', 'canceled'],
  in_progress: ['completed', 'canceled'],
  completed: [],
  no_show: [],
  canceled: [],
};

const CLASSIFICATION_OPTIONS: { value: PersonnelClassification; label: string }[] = [
  { value: 'unarmed_guard', label: 'Unarmed Guard' },
  { value: 'armed_guard', label: 'Armed Guard' },
  { value: 'ep_agent', label: 'EP Agent' },
  { value: 'detail_leader', label: 'Detail Leader' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'driver', label: 'Driver' },
  { value: 'medic', label: 'Medic' },
];

const CLASSIFICATION_LABEL = Object.fromEntries(
  CLASSIFICATION_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PersonnelClassification, string>;

function dayKey(epochMs: number): string {
  const d = new Date(epochMs);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function dayLabel(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ===========================================================================
// Schedule page
// ===========================================================================

export function SchedulePage(): JSX.Element {
  // Radix Select forbids an empty value, so 'all' is the no-filter sentinel.
  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | 'all'>('all');
  const [siteFilter, setSiteFilter] = useState<number | 'all'>('all');
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const filters = {
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(siteFilter !== 'all' ? { site_id: siteFilter } : {}),
    ...(onlyUnassigned ? { unassigned: true } : {}),
  };

  const { data, isPending, error } = useAssignments(filters);
  const sitesQuery = useSites();
  const personnelQuery = usePersonnelList({ status: 'active' });

  const assignAssignment = useAssignAssignment();
  const transitionAssignment = useTransitionAssignment();

  const sites = sitesQuery.data?.sites ?? [];
  const personnel = personnelQuery.data?.personnel ?? [];

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.name);
    return m;
  }, [sites]);

  const officerName = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of personnel) m.set(p.id, p.legal_name);
    return m;
  }, [personnel]);

  // Group the (already starts_at-sorted) assignments into day buckets for the
  // timeline board.
  const days = useMemo(() => {
    const buckets = new Map<string, { at: number; items: Assignment[] }>();
    for (const a of data?.assignments ?? []) {
      const key = dayKey(a.starts_at);
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.items.push(a);
      } else {
        buckets.set(key, { at: a.starts_at, items: [a] });
      }
    }
    return [...buckets.values()].sort((x, y) => x.at - y.at);
  }, [data]);

  const fillShift = async (a: Assignment): Promise<void> => {
    const raw = window.prompt('Assign officer — enter personnel id:');
    if (raw === null) return;
    const personnelId = Number(raw.trim());
    if (!Number.isInteger(personnelId) || personnelId <= 0) {
      toast.danger('Enter a valid personnel id.');
      return;
    }
    try {
      await assignAssignment.mutateAsync({ id: a.id, assigned_personnel_id: personnelId });
      toast.success('Officer assigned.');
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not assign the officer.'));
    }
  };

  const move = async (a: Assignment, status: AssignmentStatus): Promise<void> => {
    try {
      await transitionAssignment.mutateAsync({ id: a.id, status });
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}.`);
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not change the shift status.'));
    }
  };

  if (isPending) return <p className="rpt-status-line">Loading schedule…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view the schedule."
        : 'Failed to load the schedule.';
    return <p className="rpt-status-line rpt-status-error">{msg}</p>;
  }

  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  ];
  const siteFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All sites' },
    ...sites.map((s) => ({ value: String(s.id), label: s.name })),
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>Schedule</h1>
          <p className="page-sub">
            Shift assignments across all sites, grouped by day.
          </p>
        </div>
        {can('assignment.write') && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setShowNew(true)}>
              New shift
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as AssignmentStatus | 'all')}
          options={statusFilterOptions}
          aria-label="Filter by status"
        />
        <Select
          value={siteFilter === 'all' ? 'all' : String(siteFilter)}
          onValueChange={(v) => setSiteFilter(v === 'all' ? 'all' : Number(v))}
          options={siteFilterOptions}
          aria-label="Filter by site"
        />
        <label className="rpt-checkbox">
          <input
            type="checkbox"
            checked={onlyUnassigned}
            onChange={(e) => setOnlyUnassigned(e.target.checked)}
          />
          <span>Unfilled only</span>
        </label>
      </div>

      {days.length === 0 ? (
        <div className="rpt-card">
          <p className="rpt-empty">No shifts match these filters.</p>
        </div>
      ) : (
        <div className="sch-board">
          {days.map((day) => (
            <div className="sch-day" key={dayKey(day.at)}>
              <div className="sch-day-head">
                <span className="sch-day-date">{dayLabel(day.at)}</span>
                <span className="sch-day-count">
                  {day.items.length} shift{day.items.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="sch-shifts">
                {day.items.map((a) => {
                  const next = TRANSITIONS[a.status];
                  return (
                    <div className={`sch-shift sch-shift-${a.status}`} key={a.id}>
                      <div className="sch-shift-top">
                        <div>
                          <div className="sch-shift-window">
                            {fmtTime(a.starts_at)} – {fmtTime(a.ends_at)}
                          </div>
                          <div className="sch-shift-site">
                            {siteName.get(a.site_id) ?? `Site #${a.site_id}`}
                          </div>
                        </div>
                        <StatusBadge status={a.status}>
                          {STATUS_LABEL[a.status]}
                        </StatusBadge>
                      </div>

                      <div className="sch-shift-meta">
                        {a.assigned_personnel_id !== null ? (
                          <span className="sch-shift-officer">
                            {officerName.get(a.assigned_personnel_id) ??
                              `Officer #${a.assigned_personnel_id}`}
                          </span>
                        ) : (
                          <span className="sch-shift-unfilled">Unfilled</span>
                        )}
                        {a.required_classification && (
                          <span>
                            Requires:{' '}
                            {CLASSIFICATION_LABEL[
                              a.required_classification as PersonnelClassification
                            ] ?? a.required_classification}
                          </span>
                        )}
                        {a.notes && <span>{a.notes}</span>}
                      </div>

                      {(can('assignment.assign') || can('assignment.write')) &&
                        (next.length > 0 ||
                          a.assigned_personnel_id === null) && (
                          <div className="sch-shift-actions">
                            {can('assignment.assign') &&
                              a.status !== 'completed' &&
                              a.status !== 'canceled' &&
                              a.status !== 'no_show' && (
                                <button
                                  type="button"
                                  className="rpt-link"
                                  onClick={() => void fillShift(a)}
                                  disabled={assignAssignment.isPending}
                                >
                                  {a.assigned_personnel_id === null
                                    ? 'assign'
                                    : 'reassign'}
                                </button>
                              )}
                            {can('assignment.write') &&
                              next.map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  className={`rpt-link${
                                    s === 'canceled' || s === 'no_show'
                                      ? ' rpt-link-danger'
                                      : ''
                                  }`}
                                  onClick={() => void move(a, s)}
                                  disabled={transitionAssignment.isPending}
                                >
                                  {STATUS_LABEL[s].toLowerCase()}
                                </button>
                              ))}
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewShiftModal
          sites={sites}
          onClose={() => setShowNew(false)}
        />
      )}
    </section>
  );
}

// ===========================================================================
// New-shift modal (create assignment + qualification-mismatch warning)
// ===========================================================================

interface NewShiftModalProps {
  sites: { id: number; name: string }[];
  onClose: () => void;
}

function NewShiftModal({ sites, onClose }: NewShiftModalProps): JSX.Element {
  const createAssignment = useCreateAssignment();

  const [siteId, setSiteId] = useState<number | ''>('');
  const [postId, setPostId] = useState<number | ''>('');
  const [principalId, setPrincipalId] = useState<number | ''>('');
  const [personnelId, setPersonnelId] = useState<number | ''>('');
  const [requiredClass, setRequiredClass] = useState<PersonnelClassification | ''>('');
  const [startsAt, setStartsAt] = useState<number | null>(null);
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Lookups for the dropdowns. Posts are scoped to the chosen site; principals
  // are loaded once. Active personnel feed both the officer picker and the
  // qualification-mismatch check.
  const postsQuery = usePosts(siteId === '' ? null : siteId);
  const principalsQuery = usePrincipals();
  const personnelQuery = usePersonnelList({ status: 'active' });

  const posts = postsQuery.data?.posts ?? [];
  const principals = principalsQuery.data?.principals ?? [];
  const personnel = personnelQuery.data?.personnel ?? [];

  // Qualification mismatch: warn (but don't block — the API allows it) when the
  // chosen officer's classifications don't include the shift's required one.
  const selectedOfficer =
    personnelId === '' ? null : personnel.find((p) => p.id === personnelId) ?? null;
  const mismatch =
    requiredClass !== '' &&
    selectedOfficer !== null &&
    !selectedOfficer.classifications.includes(requiredClass);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    if (siteId === '') {
      setFormError('A site is required.');
      return;
    }
    if (startsAt === null || endsAt === null) {
      setFormError('Start and end times are required.');
      return;
    }
    if (endsAt <= startsAt) {
      setFormError('End time must be after the start time.');
      return;
    }
    try {
      await createAssignment.mutateAsync({
        site_id: siteId,
        post_id: postId === '' ? undefined : postId,
        principal_id: principalId === '' ? undefined : principalId,
        assigned_personnel_id: personnelId === '' ? undefined : personnelId,
        starts_at: startsAt,
        ends_at: endsAt,
        required_classification: requiredClass === '' ? undefined : requiredClass,
        notes: notes.trim() || undefined,
      });
      toast.success('Shift created.');
      onClose();
    } catch (err) {
      setFormError(firstFieldError(err, 'Could not create the shift.'));
    }
  };

  const busy = createAssignment.isPending;

  return (
    <Dialog
      title="New shift"
      open
      busy={busy}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="new-shift-form" variant="primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create shift'}
          </Button>
        </>
      }
    >
      <form id="new-shift-form" className="rpt-form" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="Site" required>
            <select
              className="form-input"
              value={siteId === '' ? '' : String(siteId)}
              onChange={(e) => {
                setSiteId(e.target.value === '' ? '' : Number(e.target.value));
                setPostId('');
              }}
              disabled={busy}
            >
              <option value="">Select a site…</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Post">
            <select
              className="form-input"
              value={postId === '' ? '' : String(postId)}
              onChange={(e) =>
                setPostId(e.target.value === '' ? '' : Number(e.target.value))
              }
              disabled={busy || siteId === ''}
            >
              <option value="">No specific post</option>
              {posts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Shift start" required>
            <DateInput
              value={startsAt}
              onChange={setStartsAt}
              withTime
              disabled={busy}
            />
          </FormField>
          <FormField label="Shift end" required>
            <DateInput
              value={endsAt}
              onChange={setEndsAt}
              withTime
              disabled={busy}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Required classification">
            <select
              className="form-input"
              value={requiredClass}
              onChange={(e) =>
                setRequiredClass(e.target.value as PersonnelClassification | '')
              }
              disabled={busy}
            >
              <option value="">None</option>
              {CLASSIFICATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Assign officer">
            <select
              className="form-input"
              value={personnelId === '' ? '' : String(personnelId)}
              onChange={(e) =>
                setPersonnelId(e.target.value === '' ? '' : Number(e.target.value))
              }
              disabled={busy}
            >
              <option value="">Leave open</option>
              {personnel.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.legal_name}
                </option>
              ))}
            </select>
          </FormField>
        </FormRow>

        {principals.length > 0 && (
          <FormField label="Principal (EP detail)">
            <select
              className="form-input"
              value={principalId === '' ? '' : String(principalId)}
              onChange={(e) =>
                setPrincipalId(e.target.value === '' ? '' : Number(e.target.value))
              }
              disabled={busy}
            >
              <option value="">None</option>
              {principals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name}
                </option>
              ))}
            </select>
          </FormField>
        )}

        {mismatch && selectedOfficer && (
          <p className="sch-warn" role="alert">
            {selectedOfficer.legal_name} is not classified as{' '}
            {CLASSIFICATION_LABEL[requiredClass as PersonnelClassification]}. You
            can still assign them, but verify their qualifications before the
            shift starts.
          </p>
        )}

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Special instructions, gear, access details…"
            maxLength={10_000}
            disabled={busy}
          />
        </FormField>

        {formError && (
          <p className="rpt-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </Dialog>
  );
}
